import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Box,
    Button,
    Container,
    LinearProgress,
    Slide,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, Path, useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
    useAddReviewMutation,
    useGetUserReviewForBookingQuery,
    useUpdateReviewMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import { ExperienceType } from '../../enums/experience';
import toast from '../../shared/Toast';
import { StepBody } from './Steps';
import { TAGS_BY_TYPE } from './tagPresets';
import {
    buildReviewSchema,
    categoryReviewSchema,
    EMPTY_TAGS_BY_STEP,
    ReviewFormValues,
    StepKey,
} from './types';
import {
    asPath,
    isSchemaStep,
    stepTitlesForType,
    transformExistingReviewToFormData,
} from './utils';

interface ReviewWizardProps {
    bookingId: number;
    experienceType: ExperienceType;
    existingReview?: any;
    isReadOnly?: boolean;
    onSuccess?: () => void;
}

const ReviewWizard: React.FC<ReviewWizardProps> = ({
    bookingId,
    experienceType,
    existingReview,
    isReadOnly = false,
    onSuccess,
}) => {
    const [searchParams] = useSearchParams();
    const ratingParam = searchParams.get('rating');
    const { refetch } = useGetUserReviewForBookingQuery(bookingId);

    const [addReview, { isLoading: uploadingReview }] = useAddReviewMutation();
    const [updateReview, { isLoading: updatingReview }] =
        useUpdateReviewMutation();

    const TAGS = useMemo(
        () => TAGS_BY_TYPE[experienceType] ?? EMPTY_TAGS_BY_STEP,
        [experienceType],
    );

    const STEPS = useMemo(
        () => stepTitlesForType(experienceType),
        [experienceType],
    );

    const currentSchema = useMemo(
        () => buildReviewSchema(experienceType),
        [experienceType],
    );

    const getDefaultValues = (): ReviewFormValues => {
        const existingData = transformExistingReviewToFormData(existingReview);

        if (existingReview) {
            return existingData as ReviewFormValues;
        }

        return {
            rating: 0,
            reviewText: '',
            photos: [],
            isAnonymous: false,
            cleanliness: undefined,
            accuracy: undefined,
            communication: undefined,
            access: undefined,
            value: undefined,
        };
    };

    const methods = useForm<ReviewFormValues>({
        resolver: zodResolver(currentSchema),
        mode: 'onChange',
        defaultValues: getDefaultValues(),
    });

    const { getValues, setValue, trigger, handleSubmit, control, unregister } =
        methods;

    const totalSteps = STEPS.length;
    const reviewStepIndex = STEPS.findIndex((step) => step.key === 'review');
    const [stepIndex, setStepIndex] = useState(
        isReadOnly ? Math.max(reviewStepIndex, 0) : 0,
    );
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>(
        'left',
    );
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const current = STEPS[stepIndex];

    const stepFieldNames: Record<
        Exclude<StepKey, 'review' | 'submitted'>,
        Path<ReviewFormValues>[]
    > = {
        overall: [asPath('rating')],
        cleanliness: [
            asPath('cleanliness.rating'),
            asPath('cleanliness.positives'),
            asPath('cleanliness.negatives'),
            asPath('cleanliness.otherTextPositive'),
            asPath('cleanliness.otherTextNegative'),
        ],
        accuracy: [
            asPath('accuracy.rating'),
            asPath('accuracy.positives'),
            asPath('accuracy.negatives'),
            asPath('accuracy.otherTextPositive'),
            asPath('accuracy.otherTextNegative'),
        ],
        communication: [
            asPath('communication.rating'),
            asPath('communication.positives'),
            asPath('communication.negatives'),
            asPath('communication.otherTextPositive'),
            asPath('communication.otherTextNegative'),
        ],
        access: [
            asPath('access.rating'),
            asPath('access.positives'),
            asPath('access.negatives'),
            asPath('access.otherTextPositive'),
            asPath('access.otherTextNegative'),
        ],
        value: [
            asPath('value.rating'),
            asPath('value.positives'),
            asPath('value.negatives'),
            asPath('value.otherTextPositive'),
            asPath('value.otherTextNegative'),
        ],
        details: [
            asPath('reviewText'),
            asPath('photos'),
            asPath('isAnonymous'),
        ],
    };

    const safeKey: Exclude<StepKey, 'review' | 'submitted'> = isSchemaStep(
        current.key,
    )
        ? current.key
        : 'overall';

    const watchedCurrent = useWatch({
        control: control,
        name: stepFieldNames[safeKey] as any,
    });

    const [currentStepValid, setCurrentStepValid] = useState(false);

    const isOptionalStep =
        current.key === 'cleanliness' ||
        current.key === 'accuracy' ||
        current.key === 'communication' ||
        current.key === 'access' ||
        current.key === 'value';

    useEffect(() => {
        const values = getValues();
        let isValid = true;
        if (current.key === 'review') isValid = true;
        else if (current.key === 'overall') {
            isValid =
                typeof values.rating === 'number' &&
                values.rating >= 1 &&
                values.rating <= 5;
        } else if (current.key === 'details') {
            isValid =
                typeof values.reviewText === 'string' &&
                values.reviewText.length >= 20 &&
                values.reviewText.length <= 1000;
        } else {
            const stepData = (values as any)[current.key];
            // For optional steps, undefined is valid
            if (stepData === undefined && isOptionalStep) {
                isValid = true;
            } else if (stepData === undefined) {
                isValid = false;
            } else {
                if (
                    stepData &&
                    typeof stepData.rating === 'number' &&
                    stepData.rating >= 1 &&
                    stepData.rating <= 5
                ) {
                    isValid = categoryReviewSchema.safeParse(stepData).success;
                } else {
                    isValid = false;
                }
            }
        }
        setCurrentStepValid(isValid);
    }, [current.key, watchedCurrent, isOptionalStep]);

    const onSubmit = async (data: ReviewFormValues) => {
        try {
            if (!existingReview) {
                navigateStep(1, true);
            }
            const payload: Record<string, unknown> = {
                rating: data.rating,
                reviewText: data.reviewText,
                photos: data.photos,
                isAnonymous: data.isAnonymous,
            };

            if (data.accuracy) payload.accuracy = data.accuracy;
            if (data.communication) payload.communication = data.communication;
            if (data.access) payload.access = data.access;
            if (data.value) payload.value = data.value;

            if (experienceType !== ExperienceType.Activity && data.cleanliness)
                payload.cleanliness = data.cleanliness;

            if (existingReview) {
                await updateReview({
                    id: existingReview.id,
                    data: payload,
                }).unwrap();
                toast.success('Review updated successfully!');
                refetch();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                const finalPayload = { booking: { id: bookingId }, ...payload };
                await addReview(finalPayload).unwrap();
                refetch();
                toast.success('Review submitted successfully!');
            }
        } catch (err: any) {
            console.error(err);
            const errorMessage = existingReview
                ? 'Failed to update review. Please try again.'
                : 'Failed to submit review. Please try again.';
            toast.error(err?.message || err?.data?.message || errorMessage);
        }
    };

    const navigateStep = async (
        dir: 1 | -1,
        validateBeforeNav: boolean = true,
    ) => {
        if (onSuccess && isLast) {
            onSuccess();
            return;
        }
        if (validateBeforeNav && isSchemaStep(current.key)) {
            const ok = await trigger(stepFieldNames[current.key], {
                shouldFocus: true,
            });
            if (!ok) return;
        }

        setSlideDirection(dir === 1 ? 'left' : 'right');

        const next = stepIndex + dir;
        if (next >= 0 && next < totalSteps) {
            setShouldAnimate(true);
            setIsAnimating(true);

            setTimeout(() => {
                setStepIndex(next);
                setTimeout(() => {
                    setIsAnimating(false);
                }, 50);
            }, 100);
        }
    };

    const skipStep = () => {
        if (
            isSchemaStep(current.key) &&
            current.key !== 'overall' &&
            current.key !== 'details'
        ) {
            const keyToClear = current.key as Exclude<StepKey, 'review'>;

            navigateStep(1, false);

            // Clear after navigation to avoid effects writing nested values back
            setTimeout(() => {
                try {
                    unregister?.(keyToClear as any);
                } catch {}
                setValue(
                    keyToClear as any,
                    undefined as any,
                    {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                    } as any,
                );
            }, 160);
            return;
        }

        navigateStep(1, false);
    };
    const isFirst = stepIndex === 0;
    const isSecondLast = stepIndex === totalSteps - 2;
    const isLast = stepIndex === totalSteps - 1;

    const percent = Math.round(
        ((stepIndex + 1) /
            (existingReview && !isLast ? totalSteps - 1 : totalSteps)) *
            100,
    );

    useEffect(() => {
        if (ratingParam) {
            setValue('rating', Number(ratingParam));
        }
    }, [ratingParam]);

    return (
        <FormProvider {...methods}>
            <Container maxWidth='md' sx={{ py: 2 }}>
                <Stack spacing={2} sx={{ minHeight: '700px' }}>
                    <Box
                        sx={{
                            paddingTop: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: current.key === 'submitted' ? 2 : 0.5,
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: 'bold', fontSize: '22px' }}
                        >
                            {isReadOnly ? 'Your review' : current.title}{' '}
                            {isOptionalStep && (
                                <span
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 'normal',
                                    }}
                                >
                                    (Optional)
                                </span>
                            )}
                        </Typography>
                        {!isReadOnly && current.subtitle && (
                            <Typography variant='body3' color='text.primary'>
                                {current.subtitle}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Slide
                            direction={slideDirection}
                            in={true}
                            timeout={shouldAnimate ? 300 : 0}
                            key={current.key}
                        >
                            <Box>
                                <StepBody
                                    key={current.key}
                                    currentKey={current.key}
                                    onSubmit={handleSubmit(onSubmit)}
                                    tags={TAGS}
                                    visibleKeys={STEPS.map((s) => s.key)}
                                />
                            </Box>
                        </Slide>
                    </Box>
                    {!isReadOnly ? (
                        <Box
                            role='progressbar'
                            aria-valuenow={percent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <LinearProgress
                                variant='determinate'
                                value={percent}
                                sx={{ borderRadius: 999 }}
                            />
                        </Box>
                    ) : null}

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent='space-between'
                    >
                        {isReadOnly ? (
                            <Stack
                                direction='row'
                                justifyContent='center'
                                sx={{ width: '100%' }}
                            >
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={onSuccess}
                                >
                                    Close
                                </Button>
                            </Stack>
                        ) : (
                            <Stack
                                direction='row'
                                spacing={1}
                                sx={{
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    display: 'flex',
                                }}
                            >
                                <Box>
                                    {!isLast && (
                                        <Button
                                            variant='outlined'
                                            startIcon={<ArrowBackIcon />}
                                            disabled={isFirst || isAnimating}
                                            onClick={() =>
                                                navigateStep(-1, false)
                                            }
                                        >
                                            Back
                                        </Button>
                                    )}
                                </Box>

                                <Stack direction='row' spacing={1}>
                                    {isOptionalStep && (
                                        <Button
                                            variant='text'
                                            disabled={isAnimating}
                                            onClick={skipStep}
                                        >
                                            Skip
                                        </Button>
                                    )}
                                    <Button
                                        variant='contained'
                                        endIcon={
                                            isLast ? undefined : (
                                                <ArrowForwardIcon />
                                            )
                                        }
                                        disabled={
                                            (!currentStepValid ||
                                                isAnimating) &&
                                            !isLast
                                        }
                                        onClick={() =>
                                            current.key === 'review'
                                                ? handleSubmit(onSubmit)()
                                                : navigateStep(1, true)
                                        }
                                    >
                                        {isSecondLast
                                            ? existingReview
                                                ? 'Update'
                                                : 'Submit'
                                            : isLast
                                              ? 'Close'
                                              : 'Next'}
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </Container>
        </FormProvider>
    );
};

export default ReviewWizard;
export { ExperienceType };
